import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";
import { Opportunity, IssueFeedbackComment } from "@/opportunity/opportunity";
import { useSubmitFeedback } from "@/opportunity/api";

const COMMENT_OPTIONS: { value: IssueFeedbackComment; label: string; description: string }[] = [
  { value: "dont_care", label: "Not relevant", description: "This doesn't apply to my work" },
  { value: "not_useful", label: "Not useful", description: "The alert isn't actionable" },
  { value: "known_issue", label: "Already known", description: "I'm already aware of this issue" },
  { value: "other", label: "Other", description: "Something else — tell us below" },
];

const UsefulFeedbackModal = ({
  opportunity,
  onClose,
}: {
  opportunity: Opportunity;
  onClose: () => void;
}) => {
  const [step, setStep] = useState<"initial" | "thumbs_up_text" | "thumbs_down_comment" | "thumbs_down_other" | "done">("initial");
  const [text, setText] = useState("");
  const submitFeedback = useSubmitFeedback(opportunity.id);

  const handleThumbsUp = () => {
    setStep("thumbs_up_text");
  };

  const submitThumbsUp = () => {
    submitFeedback.mutate({ useful_meter: "thumbs_up", text: text.trim() || undefined }, {
      onSuccess: () => setStep("done"),
    });
  };

  const handleComment = (comment: IssueFeedbackComment) => {
    if (comment === "other") {
      setStep("thumbs_down_other");
      return;
    }
    submitFeedback.mutate({ useful_meter: "thumbs_down", comment, text: text.trim() || undefined }, {
      onSuccess: () => setStep("done"),
    });
  };

  const submitOther = () => {
    submitFeedback.mutate({ useful_meter: "thumbs_down", comment: "other", text: text.trim() || undefined }, {
      onSuccess: () => setStep("done"),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">Feedback</p>
            <h3 className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{opportunity.name}</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-3 shrink-0 text-gray-400 hover:text-gray-600 transition-colors rounded-full p-1 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-5">
          {step === "initial" && (
            <>
              <p className="text-sm text-gray-500 mb-4">Was this alert useful?</p>
              <div className="flex gap-3">
                <button
                  onClick={handleThumbsUp}
                  disabled={submitFeedback.isPending}
                  className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-green-300 bg-green-50/50 hover:border-green-400 hover:bg-green-50 transition-all group"
                >
                  <ThumbsUp size={22} className="text-green-500 group-hover:text-green-600 transition-colors" />
                  <span className="text-sm font-medium text-green-700 group-hover:text-green-800">Yes, useful</span>
                </button>
                <button
                  onClick={() => setStep("thumbs_down_comment")}
                  disabled={submitFeedback.isPending}
                  className="flex-1 flex flex-col items-center gap-2 py-4 rounded-xl border-2 border-red-300 bg-red-50/50 hover:border-red-400 hover:bg-red-50 transition-all group"
                >
                  <ThumbsDown size={22} className="text-red-500 group-hover:text-red-600 transition-colors" />
                  <span className="text-sm font-medium text-red-700 group-hover:text-red-800">Not useful</span>
                </button>
              </div>
            </>
          )}

          {step === "thumbs_up_text" && (
            <>
              <p className="text-sm text-gray-500 mb-3">Any additional comments? (optional)</p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Tell us more..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent"
                rows={3}
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={submitThumbsUp}
                  disabled={submitFeedback.isPending}
                  className="flex-1 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-medium transition-colors"
                >
                  Submit
                </button>
                <button
                  onClick={() => { setText(""); setStep("initial"); }}
                  className="py-2 px-4 rounded-xl text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Back
                </button>
              </div>
            </>
          )}

          {step === "thumbs_down_comment" && (
            <>
              <p className="text-sm text-gray-500 mb-3">What's the reason?</p>
              <div className="flex flex-col gap-2">
                {COMMENT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleComment(option.value)}
                    disabled={submitFeedback.isPending}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-gray-100 hover:border-red-200 hover:bg-red-50 transition-all text-left group"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700 group-hover:text-red-700">{option.label}</span>
                      <span className="text-xs text-gray-400">{option.description}</span>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setText(""); setStep("initial"); }}
                className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Back
              </button>
            </>
          )}

          {step === "thumbs_down_other" && (
            <>
              <p className="text-sm text-gray-500 mb-3">Tell us what's wrong with this alert:</p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe the issue..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-transparent"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={submitOther}
                  disabled={submitFeedback.isPending || !text.trim()}
                  className="flex-1 py-2 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                >
                  Submit
                </button>
                <button
                  onClick={() => { setText(""); setStep("thumbs_down_comment"); }}
                  className="py-2 px-4 rounded-xl text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Back
                </button>
              </div>
            </>
          )}

          {step === "done" && (
            <div className="flex flex-col items-center py-4 gap-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <ThumbsUp size={18} className="text-green-500" />
              </div>
              <p className="text-sm font-medium text-gray-700">Thanks for your feedback!</p>
              <p className="text-xs text-gray-400">This helps improve future alerts.</p>
              <button
                onClick={onClose}
                className="mt-2 text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsefulFeedbackModal;